import { graphql } from 'gatsby';
import React, { Fragment, memo, useCallback, useState } from 'react';
import UpstreamGallery from 'react-image-lightbox';
import { useIntl } from 'react-intl';
import AddIcon from '../images/add.svg';
import { useDispatch } from '../store';
import style from './image-gallery.module.scss';
import { assert } from '../utils';
import IntlString from './intl-string';
import RatioImage from './ratio-image';

// "fixed" Fragment copied from
// https://github.com/gatsbyjs/gatsby/blob/b7e6bb1f730a02ac232dede97d7609e01869f6a7/packages/gatsby-transformer-sharp/src/fragments.js

export const query = graphql`
	fragment ImageGallery on Image {
		title {
			...IntlString
		}
		copyright {
			...IntlString
		}
		...RatioImage
		local {
			childImageSharp {
				imageGalleryFullImage: fixed(width: 1920) {
					src
				}
			}
		}
	}
`;

/**
 * @param {import("../../tmp/graphql-types").ImageGalleryFragment} e
 * @return {React.ReactNode}
 */
function defaultRenderTitle(e) {
	if (!e.title) {
		return null;
	}
	return (
		<IntlString {...e.title} />
	);
}

/**
 * @param {import("../../tmp/graphql-types").ImageGalleryFragment} e
 * @return {React.ReactNode}
 */
function defaultRenderCaption(e) {
	if (!e.copyright) {
		return null;
	}
	return (
		<IntlString {...e.copyright} />
	);
}

/**
 * @template T
 * @template R
 * @typedef {(entry: T) => R} ImageGalleryMapper
 */

/**
 * @template {import("../../tmp/graphql-types").ImageGalleryFragment} T
 * @param {{
 *   imageStartIndex?: number,
 *   images: Array<T>,
 *   renderTitle?: import('./image-gallery').ImageGalleryMapper<T, React.ReactNode | undefined>,
 *   renderCaption?: import('./image-gallery').ImageGalleryMapper<T, React.ReactNode | undefined>,
 * }} props
 */
function ImageGallery({
	imageStartIndex = 0,
	images,
	renderTitle = defaultRenderTitle,
	renderCaption = defaultRenderCaption,
}) {
	const intl = useIntl();
	const dispatch = useDispatch();

	/** @type {[number | null, React.Dispatch<React.SetStateAction<number | null>>]} */
	const currIndex_ = useState(null);
	const [currIndex, setCurrIndex_] = currIndex_;

	const setCurrIndex = useCallback(
		(newIndex) => {
			dispatch(newIndex !== null ? {type: 'overlayPage'} : {type: 'deOverlayPage'});
			setCurrIndex_(newIndex);
		},
		[dispatch, setCurrIndex_],
	);

	if (images.length === 0) {
		return null;
	}

	assert(0 <= imageStartIndex && imageStartIndex < images.length);

	return (
		<Fragment>
			<button
				className={style.base}
				onClick={() => setCurrIndex(imageStartIndex)}
			>
				<RatioImage
					className={style.posterImage}
					{...images[imageStartIndex]}
				/>
				<AddIcon className={style.zoomIcon} />
			</button>

			{currIndex !== null && (() => {
				const prevIndex = (currIndex + images.length - 1) % images.length;
				const nextIndex = (currIndex + 1) % images.length;
				const prevImg = images[prevIndex];
				const nextImg = images[nextIndex];
				const currImg = images[currIndex];

				const currImgSrc = currImg.local?.childImageSharp?.imageGalleryFullImage?.src;

				if (currImgSrc === undefined) {
					return null;
				}

				return (
					<UpstreamGallery
						mainSrc={currImgSrc}
						nextSrc={nextImg.local?.childImageSharp?.imageGalleryFullImage?.src}
						prevSrc={prevImg.local?.childImageSharp?.imageGalleryFullImage?.src}
						imageTitle={renderTitle(currImg)}
						imageCaption={renderCaption(currImg)}
						zoomInLabel={intl.formatMessage({id: 'gallery.zoomIn'})}
						zoomOutLabel={intl.formatMessage({id: 'gallery.zoomOut'})}
						closeLabel={intl.formatMessage({id: 'gallery.close'})}
						prevLabel={intl.formatMessage({id: 'gallery.prev'})}
						nextLabel={intl.formatMessage({id: 'gallery.next'})}
						onCloseRequest={() => setCurrIndex(null)}
						onMovePrevRequest={() => setCurrIndex(prevIndex)}
						onMoveNextRequest={() => setCurrIndex(nextIndex)}
						reactModalProps={{
							shouldReturnFocusAfterClose: false,
						}}
						reactModalStyle={{
							overlay: {
								zIndex: 5000,
							},
						}}
						wrapperClassName={style.galleryWrapper}
					/>
				);
			})()}
		</Fragment>
	);
}

export default memo(ImageGallery);
